import { CommonDialogs, CommonTooltips } from "@mcleod/common";
import { Button, DataSourceExecutionEvent, DataSourceMode, EditRowDecorator, Image, Layout, Snackbar, TableRow, TableRowDisplayEvent, Toast } from "@mcleod/components";
import { Api, CompanySettings, ModelRow, StringUtil } from "@mcleod/core";
import { DuplicateId, DuplicateIdProps } from "@mcleod/dispatch";
import { ImageName } from "@mcleod/images";
import { AutogenLayoutResponsibilityCode } from "./autogen/AutogenLayoutResponsibilityCode";
import { ResponsibilityCodeUser } from "./ResponsibilityCodeUser";

const PAY_COMMISSION_CODE_ASSIGNED = "A";
const PAY_COMMISSION_OPERATIONS = "O";
const PAY_COMMISSION_DISPATCHER = "D";
const PAY_COMMISSION_NEVER = "N";

export class ResponsibilityCode extends AutogenLayoutResponsibilityCode {

    private layoutResponsibilityCodeUser = Layout.getLayout("lme/general/ResponsibilityCodeUser") as ResponsibilityCodeUser;
    private imageUpdateAgencyUser: Image;
    private buttonAddAgencyUser: Button;

    override onLoad() {
        this.validateResponsibilityCodeExist();
        const isCommissionEnabled: boolean = CompanySettings.get()?.commission_enabled;
        this.removeColumns(isCommissionEnabled);
        this.setupTableButtons()
    }

    private validateResponsibilityCodeExist() {
        const url = "lme/general/ResponsibilityCode?mode=update&key=";
        const validateProps: DuplicateIdProps = {tableName: "agency", screenName: "ResponsibilityCode", navigateToUrl: url};
        DuplicateId.configureIdTextbox(this.textboxCode, validateProps);
    }

    private removeColumns(isCommissionEnabled: boolean): void {
        if (!isCommissionEnabled) {
            const commisionsColumn = ["Pay Commision When", "Entry Role", "Dispatch Role", "Effective Date"];
            for (const caption of commisionsColumn) {
                const column = this.tableResponsibilityUsers.getColumnByCaption(caption);
                if (column) {
                    this.tableResponsibilityUsers.removeColumn(column);
                }
            }
        }
    }

    private setupTableButtons(): void {
        this.buttonAddAgencyUser = new Button({
            color: "primary",
            caption: 'Add Responsibility User',
            imageName: ImageName.add,
            tooltip: 'Add Responsibility Code before adding the Responsibility User.',
            enabled: false,
            onClick: () => this.addAgencyUser()
        });
        this.imageUpdateAgencyUser = new Image({
            name: ImageName.pencil,
            fontSize: '12',
            color: 'default.lightest',
            tooltip: 'Update selected Responsibility User',
            enabled: false,
            marginRight: 16,
            onClick: () => this.updateAgencyUser()
        });
        this.tableResponsibilityUsers.addRightTool(this.buttonAddAgencyUser);
        this.tableResponsibilityUsers.addCenterTool(this.imageUpdateAgencyUser);
        this.tableResponsibilityUsers.addSelectionListener(() => {
            const selectedRowCount = this.tableResponsibilityUsers.selectedRowCount;
            this.imageUpdateAgencyUser.enabled = selectedRowCount === 1 ? true : false;
            this.imageUpdateAgencyUser.color = selectedRowCount === 1 ? 'primary' : 'default.lightest';
            this.imageUpdateAgencyUser.tooltip = selectedRowCount === 1 ? 'Update selected Responsibility User' : null;
        });
    }

    sourceAgencyAfterExecution(event: DataSourceExecutionEvent) {
        const agencyId = this.mainDataSource.activeRow?.get("id");
        const isAgencyIdPresent = !StringUtil.isEmptyString(agencyId);
        this.buttonAddAgencyUser.enabled = isAgencyIdPresent ? true : false;
        this.buttonAddAgencyUser.tooltip = isAgencyIdPresent ?
            'Add Responsibility User':
            'Add Responsibility Code before adding the Responsibility User.'
        const carrierId = this.mainDataSource.activeRow?.get("payee");
        if (!StringUtil.isEmptyString(carrierId)) {
            CommonTooltips.setTooltipContentLayout(this.textboxPayee, carrierId, "lme/dispatch/PayeeQuickInfo", {width: 220});
        }
    }

    tableResponsibilityUsersOnRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        tableRow.addDblClickListener(() => this.updateAgencyUser());
    }

    async addAgencyUser(): Promise<void> {
        const agencyId = this.mainDataSource.activeRow?.get("id");
        const layout = this.layoutResponsibilityCodeUser as ResponsibilityCodeUser;
        new EditRowDecorator({
            title: `Add User`,
            layout: layout, fillVerticalSpace: true, width: '60%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            layoutLoadListeners: async () => {
                layout.mainDataSource.setRowsAndMode(DataSourceMode.ADD, [await layout.mainDataSource.createBlankRow()]);
                layout.activeRow.set("row_type", "U");
                layout.activeRow.set("agency_id", agencyId);
                layout.activeRow.set("pay_commissions_when", PAY_COMMISSION_NEVER);
                this.setSlideoutValues(PAY_COMMISSION_NEVER, layout);
            },
            doAfterSlideIn: () => {
                layout.textboxRowUserId.enabled = true;
            },
            addlComponents: this.addUpdateAgencyUserButton(true, agencyId, layout),
            onClose: async (cancelled: boolean) => {
                await this.onCloseSlider(layout, agencyId);
            }
        });
    }

    private async updateAgencyUser() {
        const layout = this.layoutResponsibilityCodeUser;
        const selectedRow = this.tableResponsibilityUsers.selectedRow;
        const agencyId = this.mainDataSource.activeRow?.get("id");
        if (selectedRow == null) {
            Snackbar.showWarningSnackbar("Please select the Table row to perform operation.")
            return;
        }
        const modelRow: ModelRow = selectedRow?.data;
        const rowId = modelRow?.get("row_id");
        const rowIdLookupData = selectedRow?.data?.getLookupModelData("row_id");
        const rowIdName = rowIdLookupData != null && rowIdLookupData.length > 0 ? rowIdLookupData[0]?.data?.name : null;
        const title = rowIdName ? `Update User - ${rowId} (${rowIdName})` : `Update User - Edit - ${rowId}`;
        new EditRowDecorator({
            title: title,
            layout: layout, fillVerticalSpace: true, width: '60%',
            overlayProps: { greyedBackground: true, closeOnClickOff: false },
            data: modelRow,
            layoutLoadListeners: async () => {
                const selectedPayCommissionsWhen = modelRow?.get("pay_commissions_when");
                this.setSlideoutValues(selectedPayCommissionsWhen, layout);
            },
            doAfterSlideIn: () => {
                layout.textboxRowUserId.enabled = false;
            },
            addlComponents: this.addUpdateAgencyUserButton(false, agencyId, layout),
            onClose: async (cancelled: boolean) => {
                await this.onCloseSlider(layout, agencyId);
            }
        });
    }

    addUpdateAgencyUserButton(isAddMode: boolean, agencyId: string, layout: ResponsibilityCodeUser): Button {
        const caption = isAddMode ? "Add" : "Update";
        const addUpdateButton = new Button({
            backgroundColor: "primary", borderColor: "primary", borderWidth: 1,
            caption: caption, color: "primary.reverse", rowBreak: false,
            onClick: async () =>  {
                try {
                    addUpdateButton.busy = true;
                    addUpdateButton.enabled = false;
                    const isDataSourceChanged = layout.mainDataSource.hasChanged();
                    if (isDataSourceChanged === false) {
                        CommonDialogs.showError("No changes were made. Please try again or check your input.");
                        return;
                    }

                    const rowId = layout.mainDataSource.activeRow?.get("row_id");
                    const isPrimaryAgency = layout.mainDataSource.activeRow?.get("is_primary_agency") === "Y" ? true : false
                    if (StringUtil.isEmptyString(rowId)) {
                        Snackbar.showWarningSnackbar("Please select a user to Add or Update.");
                        return;
                    }

                    const inputs = { "agency_id": agencyId, "row_id": rowId, "is_primary_agency": isPrimaryAgency, "mode": isAddMode ? "add" : "update" };
                    const response = await Api.search("lme/general/check-responsibility-user", inputs);
                    const data = response?.data[0];

                    if (data && isPrimaryAgency && !data?.is_agency_hierarchy_present) {
                        const message = "Code [" + agencyId + "] was not found in the Responsibility `hierarchy` table.";
                        Snackbar.showWarningSnackbar(message);
                        return;
                    }
                    if (data && data.is_row_id_present) {
                        const message = "User ID [" + rowId + "] has already been added for Agency " + agencyId + ".";
                        Snackbar.showWarningSnackbar(message);
                        layout.mainDataSource.activeRow.set("row_id", null);
                        return;
                    }
                    if (data && !data.valid_row_id) {
                        Snackbar.showWarningSnackbar("Invalid User ID. Please select a valid User ID.");
                        layout.mainDataSource.activeRow.set("row_id", null);
                        return;
                    }
                    let shouldOverride: boolean = false;
                    if (data && isPrimaryAgency && data?.should_override_option) {
                        const previousAgency = data?.previous_agency;
                        const message = "Responsibility [" + previousAgency + "] is already defined as the Primary Responsibility for User ID [" + rowId + "].\nDo you want to override with Responsibility [" + agencyId + "]?";
                        const dialogProps = { yesButtonCaption: "Yes", noButtonCaption: "No" };
                        const yesNoSelection = await CommonDialogs.showYesNo(message, "Override User's Primary Agency?", dialogProps);
                        if (yesNoSelection == true) {
                            shouldOverride = true
                        } else {
                            Snackbar.showWarningSnackbar("Responsibility [" + previousAgency + "] is already defined as the primary responsibility for User ID [" + rowId + "].");
                            return;
                        }
                    }
                    layout.mainDataSource.activeRow.set("should_override", shouldOverride);
                    this.postAgencyUser(isAddMode, agencyId, layout);
                }
                catch (error) {
                    CommonDialogs.showError(error);
                }
                finally {
                    addUpdateButton.busy = false;
                    addUpdateButton.enabled = true;
                }
            }
        });
        return addUpdateButton;
    }

    private async postAgencyUser(isAddMode: boolean, agencyId: any, layout: ResponsibilityCodeUser) {
        try {
            const response = await layout.mainDataSource.post();
            if (response) {
                const userId = layout.mainDataSource.activeRow.get("row_id");
                const message = isAddMode ? `Responsibility User (${userId}) Added` : `Responsibility User (${userId}) Updated`;
                Toast.showToast(message);
                await this.delay(2000);
                layout.slideOut();
                await this.sourceAgency.search({ id: agencyId });
            }
        } catch (error) {
            CommonDialogs.showError(error);
        }
    }

    async onCloseSlider(layout: ResponsibilityCodeUser, agencyId: string) {
        if (!layout.mainDataSource.hasChanged()) {
            layout.slideOut();
            this.sourceAgency.search({ id: agencyId });
            return;
        }
        const message = "Unsaved changes are present. Do you wish to leave without saving changes?";
        const dialogProps = { yesButtonCaption: "Leave", noButtonCaption: "Stay" };
        const leave = await CommonDialogs.showYesNo(message, "Leave Without Saving?", dialogProps);
        if (leave !== true) {
            return;
        }
        layout.slideOut();
        this.sourceAgency.search({ id: agencyId });
    }

    delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    public setSlideoutValues(selectedItem: string, layout: ResponsibilityCodeUser) {
        if (PAY_COMMISSION_NEVER === selectedItem) {
            layout.textboxCommOrderEntryRole.enabled = false;
            layout.textboxCommDispatcherRole.enabled = false;
            layout.textboxEffectiveDate.enabled = false;
            layout.textboxCommOrderEntryRole.required = false;
            layout.textboxCommDispatcherRole.required = false;
            layout.textboxEffectiveDate.required = false;
        }
        if (PAY_COMMISSION_DISPATCHER === selectedItem) {
            layout.textboxCommOrderEntryRole.enabled = false;
            layout.textboxCommDispatcherRole.enabled = true;
            layout.textboxEffectiveDate.enabled = true;
            layout.textboxCommOrderEntryRole.required = false;
            layout.textboxCommDispatcherRole.required = true;
            layout.textboxEffectiveDate.required = true;
        }
        if (PAY_COMMISSION_OPERATIONS === selectedItem) {
            layout.textboxCommOrderEntryRole.enabled = true;
            layout.textboxCommDispatcherRole.enabled = false;
            layout.textboxEffectiveDate.enabled = true;
            layout.textboxCommOrderEntryRole.required = true;
            layout.textboxCommDispatcherRole.required = false;
            layout.textboxEffectiveDate.required = true;
        }
        if (PAY_COMMISSION_CODE_ASSIGNED === selectedItem) {
            layout.textboxCommOrderEntryRole.enabled = true;
            layout.textboxCommDispatcherRole.enabled = true;
            layout.textboxEffectiveDate.enabled = true;
            layout.textboxCommOrderEntryRole.required = true;
            layout.textboxCommDispatcherRole.required = true;
            layout.textboxEffectiveDate.required = true;
        }
    }

}
